/**
 * Лента уведомлений на сайте — договор `GET /notifications/me`
 * (Блок 2 · Ф7 · В11-a; **Р-132, «Уведомления в вебе — только колокольчик,
 * панель и пометка (вариант Б)»**).
 *
 * Лента — это ЧТЕНИЕ ОЧЕРЕДИ `NotificationOutbox` её получателем, а не
 * отдельное хранилище. Отсюда три свойства, которые договор обязан передавать
 * сам, а не оставлять на догадку потребителя.
 *
 * ═══ 1. СОСТАВ ВИДОВ — ЗАКРЫТЫЙ РЕЕСТР, ВЫВЕДЕННЫЙ ИЗ `DELIVERABLE_KINDS` ═══
 *
 * В ленту идут все виды о работах и не идёт напоминание о незаполненном дне
 * (`BOT_DAY_FACT_REMINDER`): оно про сам отчёт о часах, на сайте его заменяет
 * экран загрузки. Легаси и «известные, но не доставляемые» виды в ленту не
 * идут тоже — формы `display` у них нет.
 *
 * Реестр записан через `satisfies`: вид, добавленный в `DELIVERABLE_KINDS`,
 * не соберётся, пока о нём не решено здесь же — «в ленте» или «нет».
 *
 * ═══ 2. СОСТОЯНИЕ ДОСТАВКИ — ЧЕТЫРЕ, И `deliveredAt` ОДИН ИХ НЕ РАЗЛИЧАЕТ ═══
 *
 * См. `messengerDeliveryState` ниже. Это ЕДИНСТВЕННОЕ место, где состояние
 * строки очереди читается для показа; вторую копию правила не заводить.
 *
 * ═══ 3. ВРЕМЯ — `occurredAt`, А НЕ СЫРОЙ `createdAt` ═══════════════════════
 *
 * Колонка `NotificationOutbox.createdAt` несёт ДВЕ шкалы (долг #223): строки,
 * поставленные Prisma, лежат в UTC, строки, поставленные сырым SQL крона, —
 * стенным временем сессии базы (на стенде и проде это Москва, +3 ч). Сервер
 * приводит обе к настоящему моменту ещё в запросе; `occurredAt` — уже момент,
 * и порядок, курсор, граница 30 дней и счёт «новых» считаются по нему.
 */
import { z } from 'zod';

import type { DeliverableKind } from './bot-notifications';
import { WorkKind } from './work';

const UuidSchema = z.string().uuid();

/**
 * Виды, которые показывает лента.
 *
 * ⚠️ `satisfies Record<Exclude<…>>` — НЕСУЩЕЕ: двенадцатый доставляемый вид не
 * соберётся, пока его не внесут сюда (или в `Exclude` рядом — если он, как
 * напоминание о дне, в ленту не идёт).
 */
export const NOTIFICATION_FEED_KINDS = {
  WORK_ASSIGNED: true,
  WORK_UNASSIGNED: true,
  WORK_ARCHIVED: true,
  WORK_DUE_CHANGED: true,
  TASK_ASSIGNED: true,
  WORK_IN_PROGRESS: true,
  WORK_ON_REVIEW: true,
  WORK_CLOSED: true,
  WORK_RETURNED: true,
  WORK_REOPENED: true,
  WORK_DUE_SOON: true,
} satisfies Record<Exclude<DeliverableKind, 'BOT_DAY_FACT_REMINDER'>, true>;

export type NotificationFeedKind = keyof typeof NOTIFICATION_FEED_KINDS;

/** Те же виды списком — для отбора в запросе и для схемы ответа. */
export const NOTIFICATION_FEED_KIND_LIST = Object.keys(
  NOTIFICATION_FEED_KINDS,
) as NotificationFeedKind[];

/**
 * Глубина ленты, дни. Совпадает с ретеншном очереди (`RETENTION_DELETE_SQL`):
 * лента не обещает того, чего в таблице уже нет.
 */
export const NOTIFICATION_FEED_WINDOW_DAYS = 30;
export const NOTIFICATION_FEED_DEFAULT_LIMIT = 20;
export const NOTIFICATION_FEED_MAX_LIMIT = 100;
/** Потолок длины курсора: base64 от пары «момент + uuid» заведомо короче. */
export const NOTIFICATION_FEED_CURSOR_MAX_LENGTH = 256;

export const NotificationFeedQuerySchema = z
  .object({
    cursor: z.string().min(1).max(NOTIFICATION_FEED_CURSOR_MAX_LENGTH).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(NOTIFICATION_FEED_MAX_LIMIT)
      .default(NOTIFICATION_FEED_DEFAULT_LIMIT),
  })
  .strict();
export type NotificationFeedQuery = z.infer<typeof NotificationFeedQuerySchema>;

// ─── Состояние доставки в мессенджер ──────────────────────────────────────

/**
 * Что случилось со строкой очереди НА СТОРОНЕ МЕССЕНДЖЕРА.
 *
 *   - `delivered`  — транспорт принял сообщение;
 *   - `suppressed` — строка закрыта БЕЗ отправки (событие устарело к моменту
 *     доставки; отправлять было уже нечего);
 *   - `dead`       — строка похоронена: исчерпаны попытки ИЛИ получатель так и
 *     не привязал бота за отведённый срок;
 *   - `pending`    — строка ещё в очереди: ждёт такта, остывания либо привязки.
 *
 * ⚠️ ПРИЧИНЫ состояние НЕ НЕСЁТ, и вывести её из строки нечем: колонки причины
 * у очереди нет (долг #291, место 3). «Не привязал бота», «канал не настроен»
 * и «пять отказов транспорта» по строке неразличимы — потребитель обязан
 * показывать ИСХОД, а не объяснение.
 */
export const MESSENGER_DELIVERY_STATES = ['delivered', 'suppressed', 'dead', 'pending'] as const;
export type MessengerDeliveryState = (typeof MESSENGER_DELIVERY_STATES)[number];

/** Три колонки строки очереди, по которым читается исход. Значения — только
 *  «пусто / не пусто»: шкала времени у них смешанная и здесь не важна. */
export interface OutboxDeliveryColumns {
  deliveredAt: Date | string | null;
  lastAttemptAt: Date | string | null;
  deadLetter: boolean;
}

/**
 * ЕДИНСТВЕННОЕ чтение исхода строки очереди для показа.
 *
 * Правило — таблица у `MARK_SUPPRESSED_SQL`
 * (`apps/api/src/bot-outbox-worker/outbox-claim.sql.ts`), и оно НЕ «доставлено
 * ⇔ `deliveredAt` заполнен»: погашенная строка тоже получает `deliveredAt`, а
 * отличает её пустой `lastAttemptAt`.
 *
 *   | Исход       | `deliveredAt` | `lastAttemptAt` | `deadLetter` |
 *   |-------------|---------------|-----------------|--------------|
 *   | доставлено  | НЕ NULL       | НЕ NULL         | false        |
 *   | погашено    | НЕ NULL       | NULL            | false        |
 *   | похоронено  | NULL          | (любое)         | true         |
 *   | в очереди   | NULL          | (любое)         | false        |
 *
 * ⚠️ У ПОХОРОНЕННОЙ `lastAttemptAt` НЕ ПРОВЕРЯЕТСЯ НАМЕРЕННО. Таблица в
 * докблоке очереди пишет у неё «НЕ NULL», и для штатных путей это так (захват
 * проставляет отметку), но строка, похороненная руками оператора мимо
 * захвата, обязана читаться похороненной, а не «в очереди».
 *
 * ═══ ПРИОРИТЕТ: `deliveredAt` СТАРШЕ `deadLetter`, И ЭТО ВЫБОР ═════════════
 *
 * Сочетание «`deliveredAt` заполнен И `deadLetter = true`» штатными путями не
 * рождается (захват берёт только строки с пустым `deliveredAt`), но базой не
 * запрещено: CHECK-а нет, а `MARK_DEAD_LETTER_SQL` только ПОДНИМАЕТ ФЛАГ —
 * `deliveredAt` он не трогает и факта доставки не стирает. Отсюда правило:
 *
 *   - `deliveredAt` + `lastAttemptAt` — транспорт сообщение ПРИНЯЛ, и флаг,
 *     поднятый позже (рукой оператора, гонкой), человека сообщения не лишил.
 *     Назвать такую строку «не доставлено» — ложь худшего рода: человек
 *     сообщение получил. Читается `delivered`;
 *   - `deliveredAt` без `lastAttemptAt` — строка закрыта БЕЗ отправки; флаг
 *     сверху нового не сообщает (отправки не было в обоих прочтениях).
 *     Читается `suppressed`.
 *
 * `deadLetter` решает исход ТОЛЬКО при пустом `deliveredAt`. Все восемь
 * сочетаний закреплены таблицей в `test/notification-feed.test.ts`.
 */
export function messengerDeliveryState(row: OutboxDeliveryColumns): MessengerDeliveryState {
  if (row.deliveredAt !== null) {
    return row.lastAttemptAt !== null ? 'delivered' : 'suppressed';
  }
  return row.deadLetter ? 'dead' : 'pending';
}

// ─── Ответ ────────────────────────────────────────────────────────────────

/**
 * Отображаемое содержание — снимок на момент постановки строки, как его
 * положил писатель (`buildWorkNotificationPayload`, SQL крона сроков).
 *
 * ⚠️ `authorName` — имя АКТОРА события (того, кто сделал), а не постановщика
 * работы; у напоминания о сроке актора нет, и поле пустое.
 */
export const NotificationFeedDisplaySchema = z
  .object({
    title: z.string().nullable(),
    projectName: z.string().nullable(),
    sectionName: z.string().nullable(),
    /** Срок работы, `YYYY-MM-DD`. */
    dueDate: z.string(),
    authorName: z.string().nullable(),
  })
  .strict();
export type NotificationFeedDisplay = z.infer<typeof NotificationFeedDisplaySchema>;

/**
 * Куда вести со строки. Читается из ЖИВОЙ работы, а не из снимка: вид работы
 * и объект в payload не лежат.
 *
 * `null` — отдать нечего: работы больше нет ЛИБО смотрящий её сейчас не видит
 * ни на одной своей читающей поверхности. Строка остаётся текстом без ссылки.
 *
 * Три булевых — факты о ЖИВОЙ работе относительно смотрящего. По ним веб
 * решает, есть ли работа на экране, куда ведёт ссылка: снятой работы нет на
 * досках, переданной другому — нет в «Моих работах» прежнего исполнителя.
 */
export const NotificationFeedWorkRefSchema = z
  .object({
    id: UuidSchema,
    kind: z.nativeEnum(WorkKind),
    projectId: UuidSchema.nullable(),
    /** Работа снята (архивирована) — на досках её больше нет. */
    archived: z.boolean(),
    /** Смотрящий — исполнитель работы СЕЙЧАС (а не на момент события). */
    viewerIsAssignee: z.boolean(),
    /** Смотрящий — постановщик работы. */
    viewerIsAuthor: z.boolean(),
  })
  .strict();
export type NotificationFeedWorkRef = z.infer<typeof NotificationFeedWorkRefSchema>;

export const NotificationFeedItemSchema = z
  .object({
    id: UuidSchema,
    kind: z.enum(NOTIFICATION_FEED_KIND_LIST as [NotificationFeedKind, ...NotificationFeedKind[]]),
    /** Настоящий момент постановки строки, ISO-8601 с поясом (см. шапку, п. 3). */
    occurredAt: z.string().datetime(),
    /** Точка напоминания о сроке; у прочих видов `null`. */
    point: z.enum(['approaching', 'due_today']).nullable(),
    display: NotificationFeedDisplaySchema,
    work: NotificationFeedWorkRefSchema.nullable(),
    /**
     * Исход строки в мессенджере (`messengerDeliveryState`).
     *
     * ⚠️ ПОЛЕ БЕЗ ПОТРЕБИТЕЛЯ В ВЕБЕ — И ЭТО РЕШЕНИЕ, А НЕ ЗАБЫТЫЙ ХВОСТ.
     * Владелец 04.10.2026 на вопрос «нужны ли под строками подписи о том,
     * дошло ли уведомление в мессенджер» ответил «Б — без подписей»: панель
     * исход не показывает. Поле и единое чтение исхода оставлены под отчёт
     * «кому доставлено», который Р-132 отложил за Ф7: у него должен быть ОДИН
     * читатель таблицы исходов, и он уже написан и подпёрт спеками.
     */
    messenger: z.enum(MESSENGER_DELIVERY_STATES),
  })
  .strict();
export type NotificationFeedItem = z.infer<typeof NotificationFeedItemSchema>;

export const NotificationFeedResponseSchema = z
  .object({
    items: z.array(NotificationFeedItemSchema).max(NOTIFICATION_FEED_MAX_LIMIT),
    /** Курсор следующей страницы; `null` — страница последняя. */
    nextCursor: z.string().nullable(),
    /**
     * Метка «видел до этого момента», ISO-8601: время БАЗЫ на момент выборки
     * МИНУС запас `NOTIFICATION_FEED_AS_OF_MARGIN_SECONDS`. Именно её клиент
     * кладёт в браузер — не `Date.now()`: часы браузера серверу не ровня.
     *
     * Запас назад — от гонки с ещё не закоммиченной мутацией; разбор — у
     * константы.
     */
    asOf: z.string().datetime(),
  })
  .strict();
export type NotificationFeedResponse = z.infer<typeof NotificationFeedResponseSchema>;

/**
 * Запас метки `asOf` назад, секунды.
 *
 * ⚠️ ЗАЧЕМ. Строка очереди видна ленте только после коммита транзакции,
 * которая её поставила, а момент строки (`createdAt`) раньше коммита. Выборка,
 * попавшая в этот промежуток, строку не видит; клиент кладёт меткой время
 * выборки; строка коммитится — и оказывается «старше метки»: в панели есть,
 * счётчик её не посчитает НИКОГДА. Потеря тихая. С запасом метка отстоит от
 * выборки на 10 секунд, и строка с моментом в этом окне остаётся «новой».
 *
 * ⚠️ ГДЕ ИМЕННО ЛЕЖИТ ПРОМЕЖУТОК — У ДВУХ ПИСАТЕЛЕЙ ПО-РАЗНОМУ, и прежняя
 * редакция этого докблока смешивала их («`createdAt` в начале транзакции
 * мутации» — для мутаций неверно):
 *
 *   - **мутации (Prisma)** кладут `createdAt` В МОМЕНТ ВСТАВКИ строки, а
 *     вставка идёт последним шагом транзакции. Промежуток до коммита —
 *     остаток транзакции, обычно миллисекунды; потолок транзакции —
 *     `timeout: 10_000`;
 *   - **крон сроков (сырой SQL)** кладёт `now()`, то есть НАЧАЛО своей
 *     транзакции. Промежуток — вся её длительность; потолок —
 *     `DUE_SOON_TX_TIMEOUT_MS = 10_000`.
 *
 * ⚠️ ЗАПАС РАВЕН ПОТОЛКУ, А НЕ БОЛЬШЕ ЕГО. Транзакция крона, дожившая ровно до
 * предела, попадает на самую границу окна; запаса «сверху» нет. Назван как
 * есть: величина покрывает всякую транзакцию короче предела, а предельная —
 * отказ сам по себе (её обрывает Prisma).
 *
 * ⚠️ ЦЕНА. Строка, попавшая в запас и УЖЕ показанная, осталась бы «новой» до
 * следующего открытия панели. Чтобы не показывать её новой дважды, клиент
 * помнит идентификаторы показанных строк моложе метки (`seenIds` ниже).
 */
export const NOTIFICATION_FEED_AS_OF_MARGIN_SECONDS = 10;

/**
 * «Новая» ли строка: моложе метки И не показана при открытой панели.
 *
 * `seenIds` — идентификаторы строк моложе метки, которые человек уже видел в
 * открытой панели. Нужны двум случаям, где одной метки мало: строка внутри
 * запаса `asOf` и строка, чей момент ПОЗЖЕ времени базы (ручная вставка в
 * чужой шкале, переключённый пояс сессии — долг #223). Без них такая строка
 * держала бы счётчик зажжённым, сколько бы раз панель ни открывали.
 */
export function isNotificationUnseen(
  item: Pick<NotificationFeedItem, 'id' | 'occurredAt'>,
  seenAsOf: string | null,
  seenIds: ReadonlySet<string> = new Set(),
): boolean {
  if (seenAsOf === null) return false;
  const seen = Date.parse(seenAsOf);
  if (Number.isNaN(seen)) return false;
  return Date.parse(item.occurredAt) > seen && !seenIds.has(item.id);
}

/**
 * Сколько строк первой страницы «новые» относительно метки.
 *
 * ⚠️ СЧЁТ ОГРАНИЧЕН ПЕРВОЙ СТРАНИЦЕЙ, и это названо, а не спрятано: при полной
 * странице новых может быть больше — потребитель показывает «N+» по флагу
 * `more`. Метки нет (`seenAsOf === null`) — новых НОЛЬ: первый заход после
 * выката не должен зажигать счётчик на всё, что накопилось за 30 дней.
 *
 * ⚠️ `more` — ТОЛЬКО когда новая ВСЯ страница и есть следующая. Одна новая из
 * двадцати при наличии следующей страницы — это ровно «1», а не «1+»: за
 * границей страницы строки старше, и новых среди них быть не может.
 */
export function countUnseenNotifications(
  page: Pick<NotificationFeedResponse, 'items' | 'nextCursor'>,
  seenAsOf: string | null,
  seenIds: ReadonlySet<string> = new Set(),
): { count: number; more: boolean } {
  const count = page.items.filter((item) => isNotificationUnseen(item, seenAsOf, seenIds)).length;
  // «Вся страница моложе метки» считается по МЕТКЕ, без `seenIds`: показанные
  // строки тоже говорят о том, что за границей страницы могут быть новые.
  const allYounger =
    page.items.length > 0 && page.items.every((item) => isNotificationUnseen(item, seenAsOf));
  return { count, more: count > 0 && allYounger && page.nextCursor !== null };
}
